/**
 * Учебная электронная таблица для ЕГЭ 3 / 18.
 * До ~40×40 — лист целиком (стены, заливка). Тысячи строк — окно виртуализации.
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import api from '../../api/client';
import { useWorkspaceSync } from './useWorkspaceSync';
import {
  colLabel,
  cellKey,
  displayValue,
  makeEvaluator,
  rawCell,
} from './spreadsheet/formulaEngine.js';
import {
  FILL_PRESETS,
  MAX_FULL_COLS,
  applyFillToSelection,
  applyWallsToSelection,
  clearWallsInSelection,
  copyFormat,
  deleteRows,
  emptySpreadsheetState,
  fillFormulas,
  getFill,
  getWalls,
  isInSelection,
  normalizeSpreadsheetState,
  selectionBounds,
  setCellValue,
  shouldVirtualize,
} from './spreadsheet/cellModel.js';

const ROW_H = 28;
const OVERSCAN = 8;

function isSheetEmpty(state) {
  const cells = state?.cells;
  if (!cells || typeof cells !== 'object') return true;
  return Object.keys(cells).length === 0;
}

function clampSel(sel, rows, cols) {
  if (!sel) return null;
  return {
    r0: Math.max(0, Math.min(rows - 1, sel.r0)),
    r1: Math.max(0, Math.min(rows - 1, sel.r1)),
    c0: Math.max(0, Math.min(cols - 1, sel.c0)),
    c1: Math.max(0, Math.min(cols - 1, sel.c1)),
  };
}

function wallStyle(walls) {
  if (!walls) return undefined;
  const w = '3px solid #e2e8f0';
  const style = {};
  if (walls.t) style.borderTop = w;
  if (walls.r) style.borderRight = w;
  if (walls.b) style.borderBottom = w;
  if (walls.l) style.borderLeft = w;
  return style;
}

export default function SpreadsheetWorkspace({
  ydoc,
  workspaceConfig,
  seedState,
  dataKey = 'data',
  onStateChange,
  readOnly = false,
  taskSlug = null,
}) {
  const initial = useMemo(() => {
    const cfg = workspaceConfig && typeof workspaceConfig === 'object' ? workspaceConfig : {};
    let rows = Number(cfg.rows) > 0 ? Number(cfg.rows) : 20;
    let cols = Number(cfg.cols) > 0 ? Number(cfg.cols) : 20;
    rows = Math.min(Math.max(1, rows), 20000);
    cols = Math.min(Math.max(1, cols), MAX_FULL_COLS);
    const base = emptySpreadsheetState(rows, cols);
    const merged = normalizeSpreadsheetState(
      {
        ...base,
        ...(cfg.cells ? { cells: cfg.cells } : {}),
        ...(cfg.fills ? { fills: cfg.fills } : {}),
        ...(cfg.walls ? { walls: cfg.walls } : {}),
        rows,
        cols,
      },
      base,
    );
    return merged;
  }, [workspaceConfig]);

  const [state, setState] = useWorkspaceSync({
    ydoc,
    initialState: initial,
    seedState: seedState ? normalizeSpreadsheetState(seedState, initial) : null,
    dataKey,
    onChange: onStateChange,
  });

  const rows = state.rows || 20;
  const cols = Math.min(state.cols || 20, MAX_FULL_COLS);
  const virtual = shouldVirtualize(state, workspaceConfig);

  const evalCell = useMemo(() => makeEvaluator(state), [state]);

  const [sel, setSel] = useState({ r0: 0, r1: 0, c0: 0, c1: 0 });
  const [active, setActive] = useState({ row: 0, col: 0 });
  const [editing, setEditing] = useState(null); // { row, col, draft }
  const [formulaBar, setFormulaBar] = useState('');
  const [paintMode, setPaintMode] = useState(false);
  const [fillColor, setFillColor] = useState(FILL_PRESETS[1].color);
  const [sheetLoading, setSheetLoading] = useState(false);
  const [sheetError, setSheetError] = useState('');
  const [sheetSource, setSheetSource] = useState('');
  const autoLoadTried = useRef(false);
  const stateRef = useRef(state);
  stateRef.current = state;

  const applySheetState = useCallback((raw) => {
    const next = normalizeSpreadsheetState(raw, emptySpreadsheetState(
      Number(raw?.rows) || 20,
      Number(raw?.cols) || 20,
    ));
    setState(next);
    setSheetSource(String(raw?.source || ''));
    setActive({ row: 0, col: 0 });
    setSel({ r0: 0, r1: 0, c0: 0, c1: 0 });
    setEditing(null);
    setFormulaBar('');
  }, [setState]);

  const loadFromTaskFile = useCallback(async ({ force = false, onlyIfEmpty = false } = {}) => {
    if (!taskSlug || readOnly) return;
    if (onlyIfEmpty && !isSheetEmpty(stateRef.current)) return;
    setSheetLoading(true);
    setSheetError('');
    try {
      const qs = force ? '?force=1' : '';
      const { data } = await api.get(`/tasks/${encodeURIComponent(taskSlug)}/spreadsheet-sheet/${qs}`);
      if (!data || typeof data !== 'object') {
        throw new Error('Пустой ответ сервера');
      }
      applySheetState(data);
    } catch (err) {
      const msg = err?.response?.data?.detail
        || err?.message
        || 'Не удалось загрузить таблицу';
      setSheetError(String(msg));
    } finally {
      setSheetLoading(false);
    }
  }, [taskSlug, readOnly, applySheetState]);

  useEffect(() => {
    autoLoadTried.current = false;
  }, [taskSlug]);

  useEffect(() => {
    if (!taskSlug || readOnly || autoLoadTried.current) return undefined;
    // Дать Yjs применить черновик; если лист пуст — подтянуть файл задачи.
    const t = window.setTimeout(() => {
      if (autoLoadTried.current) return;
      autoLoadTried.current = true;
      void loadFromTaskFile({ onlyIfEmpty: true });
    }, 700);
    return () => window.clearTimeout(t);
  }, [taskSlug, readOnly, loadFromTaskFile]);

  const dragging = useRef(null); // 'select' | 'fill'
  const fillOrigin = useRef(null);
  const scrollRef = useRef(null);
  const editInputRef = useRef(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewH, setViewH] = useState(400);

  useEffect(() => {
    const b = selectionBounds(sel);
    if (!b) return;
    const raw = rawCell(state, active.row, active.col);
    if (!editing) setFormulaBar(raw);
  }, [state, active, sel, editing]);

  useEffect(() => {
    if (editing && editInputRef.current) {
      editInputRef.current.focus();
      editInputRef.current.select();
    }
  }, [editing]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;
    const onScroll = () => setScrollTop(el.scrollTop);
    const ro = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(() => setViewH(el.clientHeight || 400))
      : null;
    el.addEventListener('scroll', onScroll, { passive: true });
    setViewH(el.clientHeight || 400);
    if (ro) ro.observe(el);
    return () => {
      el.removeEventListener('scroll', onScroll);
      if (ro) ro.disconnect();
    };
  }, [virtual]);

  const commitEdit = useCallback(() => {
    if (!editing || readOnly) {
      setEditing(null);
      return;
    }
    setState((prev) => setCellValue(prev, editing.row, editing.col, editing.draft));
    setEditing(null);
  }, [editing, readOnly, setState]);

  const startEdit = useCallback((r, c, initialDraft) => {
    if (readOnly) return;
    const raw = initialDraft !== undefined ? initialDraft : rawCell(state, r, c);
    setActive({ row: r, col: c });
    setSel({ r0: r, r1: r, c0: c, c1: c });
    setEditing({ row: r, col: c, draft: raw });
    setFormulaBar(raw);
  }, [readOnly, state]);

  const selectCell = useCallback((r, c, extend = false) => {
    if (paintMode && !readOnly) {
      setState((prev) => copyFormat(prev, active, {
        r0: r, r1: r, c0: c, c1: c,
      }));
      setPaintMode(false);
      setActive({ row: r, col: c });
      setSel({ r0: r, r1: r, c0: c, c1: c });
      return;
    }
    setActive({ row: r, col: c });
    if (extend) {
      setSel((prev) => ({
        r0: prev.r0,
        c0: prev.c0,
        r1: r,
        c1: c,
      }));
    } else {
      setSel({ r0: r, r1: r, c0: c, c1: c });
    }
  }, [paintMode, readOnly, active, setState]);

  const onCellMouseDown = (e, r, c) => {
    if (e.button !== 0) return;
    e.preventDefault();
    if (editing) commitEdit();
    if (e.shiftKey) {
      selectCell(r, c, true);
    } else {
      selectCell(r, c, false);
    }
    dragging.current = 'select';
    const onMove = (ev) => {
      const t = document.elementFromPoint(ev.clientX, ev.clientY);
      const cell = t?.closest?.('[data-ss-r]');
      if (!cell) return;
      const nr = Number(cell.getAttribute('data-ss-r'));
      const nc = Number(cell.getAttribute('data-ss-c'));
      if (Number.isFinite(nr) && Number.isFinite(nc)) {
        setSel((prev) => ({ ...prev, r1: nr, c1: nc }));
        setActive({ row: nr, col: nc });
      }
    };
    const onUp = () => {
      dragging.current = null;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const onFillHandleMouseDown = (e) => {
    if (readOnly) return;
    e.preventDefault();
    e.stopPropagation();
    const bounds = selectionBounds(sel);
    if (!bounds) return;
    dragging.current = 'fill';
    const origin = {
      row: active.row,
      col: active.col,
      sel: { ...bounds },
      dest: { ...bounds },
    };
    fillOrigin.current = origin;
    const onMove = (ev) => {
      const t = document.elementFromPoint(ev.clientX, ev.clientY);
      const cell = t?.closest?.('[data-ss-r]');
      if (!cell) return;
      const nr = Number(cell.getAttribute('data-ss-r'));
      const nc = Number(cell.getAttribute('data-ss-c'));
      if (!Number.isFinite(nr) || !Number.isFinite(nc)) return;
      const next = {
        r0: origin.sel.r0,
        c0: origin.sel.c0,
        r1: nr,
        c1: nc,
      };
      origin.dest = next;
      setSel(next);
    };
    const onUp = () => {
      dragging.current = null;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      const src = fillOrigin.current;
      fillOrigin.current = null;
      if (!src?.dest) return;
      const dest = selectionBounds(src.dest);
      if (!dest) return;
      setState((prev) => fillFormulas(prev, { row: src.row, col: src.col }, dest));
      setSel(src.dest);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const applyFormulaBar = () => {
    if (readOnly) return;
    setState((prev) => setCellValue(prev, active.row, active.col, formulaBar));
    setEditing(null);
  };

  const onDeleteRows = () => {
    if (readOnly) return;
    const b = selectionBounds(sel);
    if (!b) return;
    setState((prev) => deleteRows(prev, b.r0, b.r1));
    const nr = Math.min(b.r0, (state.rows || 1) - (b.r1 - b.r0 + 1) - 0);
    const row = Math.max(0, Math.min(nr, (state.rows || 1) - (b.r1 - b.r0) - 1));
    setActive({ row: Math.max(0, b.r0), col: b.c0 });
    setSel({
      r0: Math.max(0, b.r0),
      r1: Math.max(0, b.r0),
      c0: b.c0,
      c1: b.c0,
    });
  };

  const onKeyDown = (e) => {
    if (editing) {
      if (e.key === 'Enter') {
        e.preventDefault();
        commitEdit();
        const nr = Math.min(rows - 1, editing.row + 1);
        setActive({ row: nr, col: editing.col });
        setSel({ r0: nr, r1: nr, c0: editing.col, c1: editing.col });
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setEditing(null);
      } else if (e.key === 'Tab') {
        e.preventDefault();
        commitEdit();
        const nc = Math.min(cols - 1, editing.col + (e.shiftKey ? -1 : 1));
        setActive({ row: editing.row, col: Math.max(0, nc) });
        setSel({
          r0: editing.row,
          r1: editing.row,
          c0: Math.max(0, nc),
          c1: Math.max(0, nc),
        });
      }
      return;
    }

    if (e.key === 'F2' || e.key === 'Enter') {
      e.preventDefault();
      startEdit(active.row, active.col);
      return;
    }
    if (e.key === 'Delete' || e.key === 'Backspace') {
      if (readOnly) return;
      e.preventDefault();
      const b = selectionBounds(sel);
      if (!b) return;
      setState((prev) => {
        let next = prev;
        for (let r = b.r0; r <= b.r1; r += 1) {
          for (let c = b.c0; c <= b.c1; c += 1) {
            next = setCellValue(next, r, c, '');
          }
        }
        return next;
      });
      setFormulaBar('');
      return;
    }

    const move = (dr, dc, extend) => {
      e.preventDefault();
      const nr = Math.max(0, Math.min(rows - 1, active.row + dr));
      const nc = Math.max(0, Math.min(cols - 1, active.col + dc));
      selectCell(nr, nc, extend);
    };

    if (e.key === 'ArrowUp') move(-1, 0, e.shiftKey);
    else if (e.key === 'ArrowDown') move(1, 0, e.shiftKey);
    else if (e.key === 'ArrowLeft') move(0, -1, e.shiftKey);
    else if (e.key === 'ArrowRight') move(0, 1, e.shiftKey);
    else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
      startEdit(active.row, active.col, e.key);
    }
  };

  const b = selectionBounds(clampSel(sel, rows, cols));

  let rowStart = 0;
  let rowEnd = rows - 1;
  let topPad = 0;
  let bottomPad = 0;
  if (virtual) {
    const first = Math.max(0, Math.floor(scrollTop / ROW_H) - OVERSCAN);
    const visible = Math.ceil(viewH / ROW_H) + OVERSCAN * 2;
    rowStart = first;
    rowEnd = Math.min(rows - 1, first + visible);
    topPad = rowStart * ROW_H;
    bottomPad = Math.max(0, (rows - 1 - rowEnd) * ROW_H);
  }

  const renderCell = (r, c) => {
    const raw = rawCell(state, r, c);
    const shown = displayValue(state, r, c, evalCell);
    const isFormula = raw.startsWith('=');
    const isActive = active.row === r && active.col === c;
    const selected = isInSelection(sel, r, c);
    const isEdit = editing && editing.row === r && editing.col === c;
    const fill = getFill(state, r, c);
    const walls = getWalls(state, r, c);
    const isFillHandle = b && r === b.r1 && c === b.c1 && isActive;

    return (
      <td
        key={c}
        data-ss-r={r}
        data-ss-c={c}
        className={[
          'ss-td',
          isFormula ? 'is-formula' : '',
          selected ? 'is-selected' : '',
          isActive ? 'is-active' : '',
        ].filter(Boolean).join(' ')}
        style={{
          background: fill || undefined,
          ...wallStyle(walls),
          height: ROW_H,
        }}
        onMouseDown={(e) => onCellMouseDown(e, r, c)}
        onDoubleClick={() => startEdit(r, c)}
      >
        {isEdit ? (
          <input
            ref={editInputRef}
            className="form-input ss-cell ss-cell-edit"
            value={editing.draft}
            onChange={(e) => {
              const draft = e.target.value;
              setEditing((prev) => (prev ? { ...prev, draft } : prev));
              setFormulaBar(draft);
            }}
            onBlur={commitEdit}
            onKeyDown={onKeyDown}
            aria-label={`${colLabel(c)}${r + 1}`}
          />
        ) : (
          <div className="ss-cell-display" title={isFormula ? raw : undefined}>
            {isFormula ? shown : raw}
          </div>
        )}
        {isFillHandle && !readOnly && !editing ? (
          <span
            className="ss-fill-handle"
            onMouseDown={onFillHandleMouseDown}
            title="Протянуть формулы"
          />
        ) : null}
      </td>
    );
  };

  const colHeaders = (
    <tr>
      <th className="ss-corner" />
      {Array.from({ length: cols }, (_, c) => (
        <th key={c} className="ss-col-h">{colLabel(c)}</th>
      ))}
    </tr>
  );

  return (
    <div
      className={`spreadsheet-workspace ${virtual ? 'is-virtual' : ''}`}
      tabIndex={0}
      onKeyDown={onKeyDown}
    >
      <div className="ss-toolbar gtw-toolbar">
        <label className="ss-formula-bar">
          <span className="ss-ref">{colLabel(active.col)}{active.row + 1}</span>
          <input
            className="form-input"
            value={formulaBar}
            disabled={readOnly}
            onChange={(e) => {
              const v = e.target.value;
              setFormulaBar(v);
              if (editing
                && editing.row === active.row
                && editing.col === active.col) {
                setEditing((prev) => (prev ? { ...prev, draft: v } : prev));
              }
            }}
            onBlur={() => {
              if (readOnly) return;
              applyFormulaBar();
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                e.stopPropagation();
                applyFormulaBar();
                e.currentTarget.blur();
              } else if (e.key === 'Escape') {
                e.preventDefault();
                e.stopPropagation();
                setFormulaBar(rawCell(state, active.row, active.col));
                e.currentTarget.blur();
              } else {
                e.stopPropagation();
              }
            }}
            placeholder="=СУММ(A1:A5)"
            aria-label="Строка формул"
          />
        </label>
        <select
          className="form-input ss-fill-select"
          value={fillColor || ''}
          disabled={readOnly}
          onChange={(e) => setFillColor(e.target.value || null)}
          aria-label="Цвет заливки"
        >
          {FILL_PRESETS.map((p) => (
            <option key={p.id} value={p.color || ''}>{p.label}</option>
          ))}
        </select>
        <button
          type="button"
          className="btn btn-sm"
          disabled={readOnly || !b}
          onClick={() => setState((prev) => applyFillToSelection(prev, b, fillColor))}
        >
          Заливка
        </button>
        <button
          type="button"
          className="btn btn-sm"
          disabled={readOnly || !b}
          onClick={() => setState((prev) => applyWallsToSelection(prev, b))}
          title="Толстые границы по периметру выделения"
        >
          Стены
        </button>
        <button
          type="button"
          className="btn btn-sm"
          disabled={readOnly || !b}
          onClick={() => setState((prev) => clearWallsInSelection(prev, b))}
        >
          Снять стены
        </button>
        <button
          type="button"
          className={`btn btn-sm ${paintMode ? 'is-active-tool' : ''}`}
          disabled={readOnly}
          onClick={() => setPaintMode((v) => !v)}
          title="Скопировать формат активной ячейки на следующую"
        >
          Формат
        </button>
        <button
          type="button"
          className="btn btn-sm"
          disabled={readOnly || !b}
          onClick={onDeleteRows}
          title="Удалить выделенные строки"
        >
          Удалить строки
        </button>
        {taskSlug ? (
          <button
            type="button"
            className="btn btn-sm"
            disabled={readOnly || sheetLoading}
            onClick={() => {
              if (!isSheetEmpty(stateRef.current)
                && !window.confirm('Заменить текущий лист данными из файла задачи?')) {
                return;
              }
              void loadFromTaskFile({ force: false });
            }}
            title="Загрузить .xlsx / .ods из вложений задачи"
          >
            {sheetLoading ? 'Загрузка…' : 'Из файла'}
          </button>
        ) : null}
      </div>
      <p className="ss-hint">
        {virtual
          ? `Лист ${rows}×${cols}, видно окно строк. `
          : `Лист ${rows}×${cols}. `}
        {sheetSource ? `Файл: ${sheetSource}. ` : null}
        {sheetError ? (
          <span className="ss-sheet-error">{sheetError}</span>
        ) : null}
        Выделение мышью / Shift+стрелки; F2 — правка; угол выделения — протяжка формул.
        Формулы: СУММ, МАКС, МИН, ЕСЛИ, СЧЁТ, СЧЁТЕСЛИ(МН), СУММЕСЛИ(МН), СРЗНАЧ(ЕСЛИ), ВПР…
      </p>
      <div className="ss-scroll" ref={scrollRef}>
        <table className="ss-table">
          <thead>{colHeaders}</thead>
          <tbody>
            {virtual && topPad > 0 ? (
              <tr aria-hidden className="ss-pad-row">
                <td
                  colSpan={cols + 1}
                  style={{ height: topPad, padding: 0, border: 'none' }}
                />
              </tr>
            ) : null}
            {Array.from({ length: rowEnd - rowStart + 1 }, (_, i) => {
              const r = rowStart + i;
              return (
                <tr key={r} style={{ height: ROW_H }}>
                  <th className="ss-row-h">{r + 1}</th>
                  {Array.from({ length: cols }, (_, c) => renderCell(r, c))}
                </tr>
              );
            })}
            {virtual && bottomPad > 0 ? (
              <tr aria-hidden className="ss-pad-row">
                <td
                  colSpan={cols + 1}
                  style={{ height: bottomPad, padding: 0, border: 'none' }}
                />
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  );
}
